import type { FlagChangeRequest, FlagTarget } from "~/features/feature-flags/shared/contracts"
import {
  FEATURE_FLAGS,
  flagName,
  type FeatureFlagKey,
} from "~/features/feature-flags/shared/registry"

export const CUSTOM_DEFAULT_REASON = "Custom flags are only turned on per company."

/** Turning a custom flag on by default would ship it to companies it wasn't built for. */
export const canTurnDefaultOn = (flagKey: FeatureFlagKey) =>
  FEATURE_FLAGS[flagKey].category !== "custom"

/** Whether the `hakioFeatureFlags` switch is on, and the registry flags LaunchDarkly has. */
export type LaunchDarklyLock = {
  switchOn: boolean
  launchDarklyKeys: ReadonlySet<FeatureFlagKey>
}

export const UNLOCKED: LaunchDarklyLock = { switchOn: false, launchDarklyKeys: new Set() }

export const LAUNCHDARKLY_LOCK_REASON =
  "Change this flag in LaunchDarkly until the hakioFeatureFlags switch is on."

/** While the switch is off, the sync copies LaunchDarkly's defaults and company targeting over
 * ours, so a change here would be undone. It never touches user overrides, so they stay open. */
export const lockedByLaunchDarkly = (
  { flagKey, scope }: Pick<FlagTarget, "flagKey" | "scope">,
  lock: LaunchDarklyLock,
) => !lock.switchOn && scope !== "user" && lock.launchDarklyKeys.has(flagKey)

/** Why a change can't be applied at all, or null. The server rejects with this text; the change
 * dialog shows it as the blocker. */
export function changeProblem(change: FlagChangeRequest, lock: LaunchDarklyLock): string | null {
  if (lockedByLaunchDarkly(change, lock)) {
    return `${flagName(change.flagKey)}: ${LAUNCHDARKLY_LOCK_REASON}`
  }
  if (change.scope === "default" && change.value === true && !canTurnDefaultOn(change.flagKey)) {
    return `${flagName(change.flagKey)}: ${CUSTOM_DEFAULT_REASON}`
  }
  return null
}

/** The changes that alter a value: those whose `value` differs from the stored `expected`. */
export const effectiveChanges = (changes: readonly FlagChangeRequest[]) =>
  changes.filter((change) => change.value !== change.expected)
